import { ChevronLeft, ChevronRight } from 'lucide-react'
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { BrandLogo } from '@/components/ui/brand-logo'
import { Button } from '@/components/ui/button'

interface SplitScreenAuthLayoutProps {
  children: ReactNode
}

const CONTENT_STATES = [
  {
    title: 'Built for your brand',
    description:
      'Powered by your catalog and rules to deliver curated, relevant recommendations.',
    bgColor: 'bg-blue-100',
    imageSrc: '/assets/signup/state1.png',
    imageAlt: 'AI product recommendation chat interface',
  },
  {
    title: 'Boosting CVR',
    description:
      'Turn questions into confident purchases so fewer shoppers stall or drop off.',
    bgColor: 'bg-pink-100',
    imageSrc: '/assets/signup/state2.png',
    imageAlt: 'CVR analytics and conversion metrics',
  },
  {
    title: 'Launchable in Minutes',
    description: 'Get your AI assistant live with a quick, lightweight setup.',
    bgColor: 'bg-[#FFEDD5]',
    imageSrc: '/assets/signup/state3.png',
    imageAlt: 'Quick setup product recommendation interface',
  },
]

export function SplitScreenAuthLayout({
  children,
}: SplitScreenAuthLayoutProps) {
  const [activeSlide, setActiveSlide] = useState(0)

  // Auto-advance slides every 5 seconds, reset to first after last
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((prev) => {
        if (prev === 2) {
          return 0
        }
        return prev + 1
      })
    }, 5000)
    return () => clearInterval(timer)
  }, [])

  const nextSlide = () => {
    setActiveSlide((prev) => {
      if (prev === 2) {
        return 0
      }
      return prev + 1
    })
  }

  const prevSlide = () => {
    setActiveSlide((prev) => {
      if (prev === 0) {
        return 0
      }
      return prev - 1
    })
  }

  const getSlideClasses = (offset: number) => {
    if (offset === 0) {
      return 'translate-x-0 scale-100 opacity-100 z-20'
    }
    if (offset === 1) {
      return 'translate-x-[550px] scale-90 opacity-40 z-10'
    }
    if (offset === -1) {
      return '-translate-x-[550px] scale-90 opacity-40 z-10'
    }
    if (offset > 1) {
      return 'translate-x-[calc(2*550px)] scale-90 opacity-0 z-0'
    }
    return '-translate-x-[calc(2*550px)] scale-90 opacity-0 z-0'
  }

  return (
    <div className="min-h-screen flex gap-6 p-4 md:p-6 bg-background">
      {/* Left side - Form content */}
      <div className="flex-1 flex flex-col justify-center items-center">
        <div className="absolute left-4 top-4 md:left-6 md:top-6">
          <BrandLogo />
        </div>

        <div className="w-full max-w-[400px] px-2 md:px-0">{children}</div>
      </div>

      {/* Right side - Animated content showcase (hidden on mobile) */}
      <div className="hidden lg:flex flex-1 bg-[#0f172a] rounded-2xl overflow-hidden flex-col gap-12 items-center justify-center relative">
        {/* Top text with fade transition */}
        <div className="max-w-[350px] w-full text-center space-y-4 relative h-[120px]">
          {CONTENT_STATES.map((state, index) => (
            <div
              key={state.title}
              className={`absolute inset-0 transition-opacity duration-500 ${
                index === activeSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <p className="text-sm text-blue-400 mb-2">Vwam</p>
              <h2 className="text-2xl font-semibold text-white mb-4">
                {state.title}
              </h2>
              <p className="text-sm text-slate-200">{state.description}</p>
            </div>
          ))}
        </div>

        {/* Content cards with slide and scale transition */}
        <div className="w-full h-[283px] relative flex items-center justify-center overflow-hidden">
          {CONTENT_STATES.map((state, index) => (
            <div
              key={state.title}
              className={`absolute inset-0 max-w-[550px] mx-auto ${state.bgColor} rounded-2xl overflow-hidden transition-all duration-700 ease-in-out ${getSlideClasses(index - activeSlide)}`}
            >
              <div className="relative w-full h-full flex items-center justify-center p-6">
                <img
                  src={state.imageSrc}
                  alt={state.imageAlt}
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Navigation */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 z-30">
          <Button
            variant="ghost"
            size="sm"
            className={`w-6 h-6 p-1 text-white hover:text-white hover:bg-white/10 ${
              activeSlide === 0 ? 'opacity-30 cursor-not-allowed' : ''
            }`}
            onClick={prevSlide}
            disabled={activeSlide === 0}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>

          <div className="flex gap-1">
            {CONTENT_STATES.map((state, index) => (
              <button
                key={state.title}
                type="button"
                className={`w-2 h-2 rounded-full transition-all ${
                  index === activeSlide ? 'bg-white w-6' : 'bg-white/50'
                }`}
                onClick={() => setActiveSlide(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="w-6 h-6 p-1 text-white hover:text-white hover:bg-white/10"
            onClick={nextSlide}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
